import { Link } from "react-router-dom";
import { Shield, BookOpen, AlertTriangle, Mail, Sparkles } from "lucide-react";

/**
 * Public methodology page — explains how Affynix builds AI dossiers, what's estimated vs.
 * verified, and how to flag a hallucination. Linked from the disclaimer footer on every
 * public pitch + firm dossier. Trust-page conversion lever — a 5-minute read that turns
 * enterprise skeptics into pilots.
 *
 * No auth required, identical structure to PublicPitch's overall layout for visual continuity.
 */
export default function Methodology() {
    return (
        <div className="min-h-screen bg-black text-zinc-100" data-testid="methodology-page">
            <div className="max-w-3xl mx-auto px-6 py-16">
                {/* Eyebrow */}
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
                    // Methodology &amp; trust
                </div>
                <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-zinc-50 leading-tight mb-4">
                    How we build every Affynix dossier.
                </h1>
                <p className="text-base text-zinc-400 leading-relaxed max-w-2xl">
                    Affynix uses Claude Sonnet 4.5 to synthesize firm research from public signals.
                    This page is the honest version — what we do well, what we can&apos;t verify, and
                    how to flag anything that looks off.
                </p>

                {/* Section 1: What we read */}
                <section className="mt-12 border border-zinc-800 bg-zinc-950 p-6" data-testid="section-sources">
                    <div className="flex items-center gap-2 mb-3">
                        <BookOpen className="w-4 h-4 text-amber-400" />
                        <h2 className="font-heading font-bold text-lg text-zinc-50">What we read</h2>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                        For every dossier, Claude Sonnet 4.5 is given the company name and asked to
                        produce a structured JSON output covering:
                    </p>
                    <ul className="space-y-2 text-sm text-zinc-300">
                        {[
                            ["Company overview", "Mission, business model, market position — drawn from public corporate sites, press, and news."],
                            ["Products & pricing", "Names, descriptions, list pricing where published, and estimated revenue share."],
                            ["Financials", "Annual revenue estimates and growth rates — typically from public filings, S-1s, IPO prospectuses, and press."],
                            ["Competitors", "Direct & adjacent players based on category overlap and public market analyst coverage."],
                            ["Recent news", "Notable announcements, funding rounds, leadership changes — surfaced from the model's training cutoff."],
                        ].map(([title, desc]) => (
                            <li key={title} className="flex gap-3">
                                <span className="font-mono text-[10px] text-amber-500 mt-1 shrink-0">→</span>
                                <div><span className="text-zinc-100 font-semibold">{title}</span> · <span className="text-zinc-400">{desc}</span></div>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Section 2: What's estimated */}
                <section className="mt-6 border border-amber-500/30 bg-amber-500/[0.03] p-6" data-testid="section-estimates">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <h2 className="font-heading font-bold text-lg text-zinc-50">What&apos;s estimated (not verified)</h2>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                        These fields are <span className="text-amber-300 font-semibold">working estimates</span> — directional, but not audited.
                        Always verify before they appear in a contract, SAFE note, or press statement.
                    </p>
                    <ul className="space-y-2 text-sm text-zinc-300">
                        {[
                            "Specific revenue figures for private companies (we cannot access internal financials).",
                            "Customer counts and growth percentages for non-public firms.",
                            "Monthly revenue series (always extrapolated from annual + growth-rate estimates).",
                            "Fit scores and synergy projections (model heuristics, not market research).",
                            "ROI calculator outputs (user-adjustable scenarios — outputs reflect inputs, not Affynix predictions).",
                        ].map((line, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="font-mono text-[10px] text-amber-500 mt-1 shrink-0">⚠</span>
                                <div className="text-zinc-300">{line}</div>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Section 3: Our guardrails */}
                <section className="mt-6 border border-emerald-500/30 bg-emerald-500/[0.03] p-6" data-testid="section-guardrails">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-emerald-400" />
                        <h2 className="font-heading font-bold text-lg text-zinc-50">Our guardrails</h2>
                    </div>
                    <ul className="space-y-2 text-sm text-zinc-300">
                        <li className="flex gap-3">
                            <span className="font-mono text-[10px] text-emerald-400 mt-1 shrink-0">✓</span>
                            <div><span className="text-zinc-100 font-semibold">Structured outputs only.</span> Claude returns a strict JSON schema — no freeform paragraphs that can hide unsupported claims.</div>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-mono text-[10px] text-emerald-400 mt-1 shrink-0">✓</span>
                            <div><span className="text-zinc-100 font-semibold">Public-data only.</span> No private financials, no PII, no leaked materials. If we can&apos;t source it from a public signal, the dossier omits it.</div>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-mono text-[10px] text-emerald-400 mt-1 shrink-0">✓</span>
                            <div><span className="text-zinc-100 font-semibold">Disclaimer on every public pitch.</span> The amber-iconed AI-generated notice is permanent — we don&apos;t hide what produced the analysis.</div>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-mono text-[10px] text-emerald-400 mt-1 shrink-0">✓</span>
                            <div><span className="text-zinc-100 font-semibold">Human override.</span> The firm-library owner can edit every field — Claude provides the first draft, the operator owns the final version.</div>
                        </li>
                    </ul>
                </section>

                {/* Section 4: How to report a hallucination */}
                <section className="mt-6 border border-zinc-800 bg-zinc-950 p-6" data-testid="section-report">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <h2 className="font-heading font-bold text-lg text-zinc-50">Found something off? Let us know.</h2>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                        Models hallucinate. If you spot a figure or claim that doesn&apos;t match
                        reality, we&apos;d genuinely like to hear about it — your flag becomes a
                        prompt-tuning datapoint that improves every future dossier.
                    </p>
                    <a
                        href="mailto:admin@affynix.it?subject=Hallucination%20report"
                        data-testid="report-hallucination-link"
                        className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wider px-5 py-2.5"
                    >
                        <Mail className="w-4 h-4" /> Report a hallucination
                    </a>
                </section>

                {/* Footer */}
                <div className="mt-12 text-center font-mono text-[10px] text-zinc-700">
                    <Link to="/" className="text-amber-500 hover:text-amber-400">← Back to Affynix</Link>
                </div>
            </div>
        </div>
    );
}
